<template>
  <!-- Dev mode: no captcha needed -->
  <template v-if="provider === 'none'" />
  <GeetestCheckbox
    v-else-if="provider === 'geetest'"
    ref="widgetRef"
    :container-id="containerId"
    :auto-show="autoShow"
    @cancelled="emit('cancelled')"
    @verified="onVerified"
    @expired="onExpired"
    @error="onError"
  />
  <TurnstileCheckbox
    v-else-if="provider === 'turnstile'"
    ref="widgetRef"
    :container-id="containerId"
    @verified="onVerified"
    @expired="onExpired"
    @error="onError"
  />
  <RecaptchaCheckbox
    v-else
    ref="widgetRef"
    :container-id="containerId"
    @verified="onVerified"
    @expired="onExpired"
    @error="onError"
  />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { getCaptchaProvider, type CaptchaProvider } from '@/shared/utils/captchaConfig'
import RecaptchaCheckbox from './RecaptchaCheckbox.vue'
import TurnstileCheckbox from './TurnstileCheckbox.vue'
import GeetestCheckbox from './GeetestCheckbox.vue'

defineProps<{
  containerId?: string
  autoShow?: boolean
}>()

const emit = defineEmits<{
  (e: 'verified', token: string): void
  (e: 'expired'): void
  (e: 'error'): void
  (e: 'cancelled'): void
}>()

const provider = computed<CaptchaProvider>(() => getCaptchaProvider())

const widgetRef = ref<InstanceType<typeof RecaptchaCheckbox> | InstanceType<typeof TurnstileCheckbox> | InstanceType<typeof GeetestCheckbox> | null>(null)

const onVerified = (token: string) => {
  emit('verified', token)
}

const onExpired = () => {
  emit('expired')
}

const onError = () => {
  emit('error')
}

/**
 * Reset the widget for re-verification
 */
const reset = () => {
  widgetRef.value?.reset()
}

/**
 * Get current token
 */
const getToken = (): string => {
  return widgetRef.value?.getToken() ?? ''
}

/**
 * Trigger captcha popup (for Geetest bind mode).
 * In dev mode (provider=none), immediately emit verified with a bypass token.
 */
const showCaptcha = () => {
  if (provider.value === 'none') {
    emit('verified', 'dev-bypass')
    return
  }
  widgetRef.value?.showCaptcha?.()
}

// Expose methods for parent components
defineExpose({
  reset,
  getToken,
  showCaptcha,
  provider,
})
</script>

